import React, { useMemo, useState } from 'react';

import { ChevronDownIcon, ChevronUpIcon } from '@vlabs/icons';
import { Control } from '@vlabs/uikit';

import st from './GroupedCheckbox.module.sass';
import { GroupedCheckboxItem } from './GroupedCheckboxItem';
import type { TGroupData } from './types';

interface GroupedCheckboxProps {
  group: TGroupData;
  value: string[];
  onChange: (nextIds: string[]) => void;
  collapsible?: boolean;
}

export function GroupedCheckbox({
  group,
  value,
  onChange,
  collapsible = true,
}: GroupedCheckboxProps): React.JSX.Element {
  const [collapsed, setCollapsed] = useState<boolean>(!!group.defaultCollapsed);
  const selected = useMemo(() => new Set(value), [value]);
  const enabledValues = group.items.map((i) => i.value);

  const uniqueEnabledValues = Array.from(new Set(enabledValues));
  const hasAny = uniqueEnabledValues.length > 0;

  const checkedCount = uniqueEnabledValues.reduce(
    (acc, val) => acc + (selected.has(val) ? 1 : 0),
    0,
  );

  const allChecked = hasAny && checkedCount === uniqueEnabledValues.length;
  const indeterminate = hasAny && checkedCount > 0 && checkedCount < uniqueEnabledValues.length;

  const onToggleAll = () => {
    if (allChecked) {
      const removeSet = new Set(uniqueEnabledValues);
      const next = value.filter((v) => !removeSet.has(v));
      onChange(next);
      return;
    }

    const next = Array.from(new Set([...value, ...uniqueEnabledValues]));
    onChange(next);
  };

  const onToggleOne = (val: string, nextChecked: boolean) => {
    if (nextChecked) {
      onChange(Array.from(new Set([...value, val])));
      return;
    }

    onChange(value.filter((v) => v !== val));
  };

  const onToggleCollapse = () => {
    if (!collapsible) return;
    setCollapsed((v) => !v);
  };

  return (
    <div className={st.Group}>
      <div className={st.GroupHeader}>
        <Control.Checkbox
          checked={allChecked}
          className={st.Checkbox}
          indeterminate={indeterminate}
          label={group.label}
          onChangeValue={() => {
            onToggleAll();
            return true;
          }}
        />

        {!!collapsible && (
          <Control.IconButton
            icon={collapsed ? <ChevronDownIcon /> : <ChevronUpIcon />}
            kind="secondary"
            onClick={onToggleCollapse}
            size="small"
            variant="dimmed"
          />
        )}
      </div>

      {!collapsed && (
        <React.Fragment>
          {group.items.map((item) => (
            <GroupedCheckboxItem
              checked={selected.has(item.value)}
              key={item.id}
              label={item.label}
              onToggle={onToggleOne}
              rightSlot={item.rightSlot}
              value={item.value}
            />
          ))}
        </React.Fragment>
      )}
    </div>
  );
}
